import { useUser } from "@/hooks/useUser";
import { UserRoleEnumValue, PortfolioRoleEnumValue, ProgramRoleEnumValue } from "@backend/src/schemas/enums";
import { Navigate, Outlet } from "react-router-dom";

export type AllowedRole =
  | (typeof UserRoleEnumValue)[keyof typeof UserRoleEnumValue]
  | (typeof PortfolioRoleEnumValue)[keyof typeof PortfolioRoleEnumValue]
  | (typeof ProgramRoleEnumValue)[keyof typeof ProgramRoleEnumValue];

interface RoleGuardProps {
  /** Organisation-level roles that are allowed to access the child routes. */
  allowedRoles?: AllowedRole[];
  /** Organisation-level roles that are explicitly blocked from accessing the child routes. */
  disallowedRoles?: AllowedRole[];
  /** Where to redirect when the user's role is insufficient. Defaults to "/projects" or "/mytasks". */
  redirectTo?: string;
}

/**
 * Route-level guard that blocks access based on the user's organisation role.
 */
const RoleGuard = ({ allowedRoles, disallowedRoles, redirectTo = "/projects" }: RoleGuardProps) => {
  const { user, orgRole } = useUser();

  if (!user) {
    return null;
  }

  // If user has a disallowed role, block immediately
  if (disallowedRoles && orgRole && disallowedRoles.includes(orgRole as AllowedRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  // If no allowedRoles specified, allow through (guard only checked disallowedRoles)
  if (!allowedRoles || allowedRoles.length === 0) {
    return <Outlet />;
  }

  let hasAccess = false;

  // 1. Check direct org role (ADMINISTRATOR, PROJECT_MANAGER, etc.)
  if (orgRole && allowedRoles.includes(orgRole as AllowedRole)) {
    hasAccess = true;
  }

  // 2. Check project manager status (via assigned managed projects)
  if (!hasAccess && allowedRoles.includes(UserRoleEnumValue.PROJECT_MANAGER)) {
    if ((user.managedProjectIds?.length ?? 0) > 0) {
      hasAccess = true;
    }
  }

  // 3. Check portfolio manager status
  if (!hasAccess && allowedRoles.includes(PortfolioRoleEnumValue.PORTFOLIO_MANAGER)) {
    if ((user.managedPortfolioIds?.length ?? 0) > 0) {
      hasAccess = true;
    }
  }

  // 4. Check program manager status
  if (!hasAccess && allowedRoles.includes(ProgramRoleEnumValue.PROGRAM_MANAGER)) {
    if ((user.managedProgramIds?.length ?? 0) > 0) {
      hasAccess = true;
    }
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export default RoleGuard;
